// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rapport's "Før indberetning" checklist as data (v8 prototype `rightRapport`):
 * one row per thing BEK 496 wants settled before the report goes to the
 * municipality, and the readiness the "Indberet" button is gated on —
 * `canSend(fractions)` and every row ok.
 *
 * Rows that rest on something the contract does not carry yet say "ukendt"
 * instead of guessing (state `unknown`, which is not ok):
 *  - "Alle typer screenet" reads the part-scoped resource key labelled
 *    *Screenet* (a leksikon key in the screening template); without that key
 *    the row is unknown.
 *  - "Sagsdata komplette" and "Screening godkendt af bygherre" read the BEK
 *    496 project fields (`ProjectExtras`, gap 3), which ruxd's `ProjectInfo`
 *    carries since ruxd-6tz; the caller passes whatever it holds (the
 *    server's record, merged with the locally kept copy on an older server).
 *
 * Pure: no React, no DOM.
 */

import type { ProjectInfo, Resource, ResourceKey, Sample, SurveyFractions, SurveyType } from '../api/types';
import { canSend } from '../indberetning/model';
import { isTrue } from '../kortlaegning/cellDraft';
import type { SagsdataExtras } from './sagsdata';

/**
 * The BEK 496 Sagsdata fields as the checklist reads them (gap 3; in
 * `ProjectInfo` since ruxd-6tz). Looser than the contract: the area may be
 * text from the form, and an older server's record lacks them all.
 */
export interface ProjectExtras {
  bbr_number?: string | null;
  cadastral?: string | null;
  client?: string | null;
  client_contact?: string | null;
  coordinator?: string | null;
  municipality?: string | null;
  /** ISO date (`YYYY-MM-DD`). */
  demolition_start?: string | null;
  demolished_floor_area_m2?: number | string | null;
  /** ISO date or timestamp of the bygherre's approval of the screening. */
  screening_approved_at?: string | null;
}

export type ProjectRecord = ProjectInfo & ProjectExtras;

/**
 * The record the checklist reads: the server's project with the Sagsdata
 * extras over it (`useSagsdataExtras` — the server's value where it has the
 * key, else this browser's copy), so Overblik and Rapport judge the same
 * record. Null without a project. The extras are `mergeExtras`' already
 * typed values (the area a number, dates ISO), so nothing needs parsing here.
 */
export function projectRecord(project: ProjectInfo | null, extras: SagsdataExtras): ProjectRecord | null {
  return project ? { ...project, ...extras } : null;
}

export type CheckId = 'approved' | 'samples' | 'screened' | 'eakTons' | 'sagsdata' | 'screeningApproved';

export type CheckState = 'ok' | 'no' | 'unknown';

export interface CheckRow {
  id: CheckId;
  label: string;
  /** The subline: "4 af 8", "2 mangler", "ukendt — …". */
  detail: string;
  state: CheckState;
  /** What the Indberet hint names when the row is not ok ("3 godkendelser"); null when ok. */
  missing: string | null;
}

export interface ChecklistInput {
  types: readonly SurveyType[];
  samples: readonly Sample[];
  fractions: SurveyFractions | null;
  project: ProjectRecord | null;
  resourceKeys: readonly ResourceKey[];
  resources: readonly Resource[];
}

export interface Checklist {
  rows: CheckRow[];
  /** `canSend(fractions)` and every row ok. */
  ready: boolean;
  /** The reasons readiness fails, in row order (then the fractions'); empty when ready. */
  missing: string[];
  /**
   * `server`: the verdict is ruxd's sections (`withServerSections`), and each
   * `missing` entry is a phrase of its own ("Sagsdata mangler: Matrikel")
   * rather than a noun that follows "Mangler".
   */
  source?: 'server';
}

/** The resource key the "Alle typer screenet" row reads (by label: its id is a leksikon guid). */
export const SCREENED_KEY_LABEL = 'Screenet';

/** The Sagsdata fields the report cannot be submitted without, with their labels. */
export const SAGSDATA_REQUIRED: readonly { key: keyof ProjectRecord; label: string }[] = [
  { key: 'building_address', label: 'Adresse' },
  { key: 'bbr_number', label: 'BBR-nummer' },
  { key: 'cadastral', label: 'Matrikel' },
  { key: 'client', label: 'Bygherre' },
  { key: 'municipality', label: 'Kommune' },
  { key: 'demolition_start', label: 'Nedrivningsstart' },
  { key: 'demolished_floor_area_m2', label: 'Nedrevet etageareal' },
];

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Non-rejected types: what the counts (`all` = queue + approved) and the report cover. */
export function reportedTypes(types: readonly SurveyType[]): SurveyType[] {
  return types.filter((t) => t.review_status !== 'rejected');
}

/** A field counts as filled when it is a non-blank string or a positive number. */
export function filled(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value) && value > 0;
  if (typeof value === 'string') return value.trim() !== '';
  return false;
}

/** "21.09.2026" for an ISO date or timestamp; the raw string when it is neither. */
export function danishDate(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
}

/** A type is missing EAK when it has no code, and tons when its mass is unknown (bevaring needs none). */
export function lacksEakOrTons(t: SurveyType): boolean {
  return t.eak_code.trim() === '' || (t.mass_t === null && t.treatment !== 'bevaring');
}

/** The *Screenet* key, or null when the project has none. */
export function screenedKey(keys: readonly ResourceKey[]): ResourceKey | null {
  const want = SCREENED_KEY_LABEL.toLowerCase();
  return keys.find((k) => k.label.trim().toLowerCase() === want) ?? null;
}

/**
 * Whether a type is screened: it has parts, and every one of its resources
 * says yes on the *Screenet* key (part-scoped, so one blank part is enough to
 * leave the type unscreened).
 */
export function typeScreened(typeId: number, keyId: string, resources: readonly Resource[]): boolean {
  const own = resources.filter((r) => r.type_id === typeId);
  return own.length > 0 && own.every((r) => isTrue(r.values[keyId] ?? null));
}

function approvedRow(types: readonly SurveyType[]): CheckRow {
  const all = types.length;
  const n = types.filter((t) => t.review_status === 'approved').length;
  if (all === 0) {
    return { id: 'approved', label: 'Alle typer godkendt', detail: 'Ingen typer i kortlægningen endnu', state: 'no', missing: 'typer i kortlægningen' };
  }
  const left = all - n;
  return {
    id: 'approved',
    label: 'Alle typer godkendt',
    detail: `${n} af ${all}`,
    state: left === 0 ? 'ok' : 'no',
    missing: left === 0 ? null : plural(left, 'godkendelse', 'godkendelser'),
  };
}

function samplesRow(samples: readonly Sample[]): CheckRow {
  const all = samples.length;
  const n = samples.filter((s) => s.stage === 'svar').length;
  if (all === 0) {
    return { id: 'samples', label: 'Prøvesvar modtaget', detail: 'Ingen prøver udtaget', state: 'ok', missing: null };
  }
  const left = all - n;
  return {
    id: 'samples',
    label: 'Prøvesvar modtaget',
    detail: `${n} af ${all}`,
    state: left === 0 ? 'ok' : 'no',
    missing: left === 0 ? null : `${left} prøvesvar`,
  };
}

function screenedRow(types: readonly SurveyType[], keys: readonly ResourceKey[], resources: readonly Resource[]): CheckRow {
  const key = screenedKey(keys);
  if (!key) {
    return {
      id: 'screened',
      label: 'Alle typer screenet',
      detail: `ukendt — projektet har intet felt "${SCREENED_KEY_LABEL}"`,
      state: 'unknown',
      missing: 'screening ukendt',
    };
  }
  const all = types.length;
  const n = types.filter((t) => typeScreened(t.id, key.id, resources)).length;
  const left = all - n;
  return {
    id: 'screened',
    label: 'Alle typer screenet',
    detail: `${n} af ${all} · feltet ${key.label}`,
    state: left === 0 ? 'ok' : 'no',
    missing: left === 0 ? null : plural(left, 'screening', 'screeninger'),
  };
}

function eakTonsRow(types: readonly SurveyType[]): CheckRow {
  const n = types.filter(lacksEakOrTons).length;
  return {
    id: 'eakTons',
    label: 'EAK og tons på alle typer',
    detail: n === 0 ? 'Komplet' : `${n} mangler`,
    state: n === 0 ? 'ok' : 'no',
    missing: n === 0 ? null : `${n} med EAK/tons`,
  };
}

/** The Sagsdata fields still blank, by label. */
export function missingSagsdata(project: ProjectRecord | null): string[] {
  return SAGSDATA_REQUIRED.filter((f) => !filled(project?.[f.key])).map((f) => f.label);
}

function sagsdataRow(project: ProjectRecord | null): CheckRow {
  if (!project) {
    return { id: 'sagsdata', label: 'Sagsdata komplette', detail: 'Ingen sagsdata endnu', state: 'no', missing: 'sagsdata' };
  }
  const missing = missingSagsdata(project);
  return {
    id: 'sagsdata',
    label: 'Sagsdata komplette',
    detail: missing.length === 0 ? 'Adresse, BBR, matrikel, bygherre' : `Mangler: ${missing.join(', ')}`,
    state: missing.length === 0 ? 'ok' : 'no',
    missing: missing.length === 0 ? null : 'sagsdata',
  };
}

function screeningApprovedRow(project: ProjectRecord | null): CheckRow {
  const at = project?.screening_approved_at;
  if (typeof at === 'string' && at.trim() !== '') {
    return { id: 'screeningApproved', label: 'Screening godkendt af bygherre', detail: danishDate(at), state: 'ok', missing: null };
  }
  return {
    id: 'screeningApproved',
    label: 'Screening godkendt af bygherre',
    detail: 'ukendt — udfyldes under Sagen i Overblik',
    state: 'unknown',
    missing: 'bygherrens godkendelse af screeningen',
  };
}

/** Why the fractions cannot be sent, worded to follow "Mangler …". */
export function fractionsReason(f: SurveyFractions | null): string {
  if (!f) return 'fraktionerne (ikke indlæst)';
  if (f.blocking.length === 0) return 'fraktioner at indberette';
  return `afklaring af ${plural(f.blocking_types, 'type', 'typer')} i fraktionerne`;
}

/** The "Før indberetning" rows and the readiness they add up to. */
export function buildChecklist(input: ChecklistInput): Checklist {
  const types = reportedTypes(input.types);
  const rows = [
    approvedRow(types),
    samplesRow(input.samples),
    screenedRow(types, input.resourceKeys, input.resources),
    eakTonsRow(types),
    sagsdataRow(input.project),
    screeningApprovedRow(input.project),
  ];
  const missing = rows.flatMap((r) => (r.state === 'ok' || r.missing === null ? [] : [r.missing]));
  const sendable = input.fractions !== null && canSend(input.fractions);
  // The fractions block for the same reasons the rows already name; only say
  // so on their own when every row is fine (e.g. nothing to report).
  if (!sendable && missing.length === 0) missing.push(fractionsReason(input.fractions));
  return { rows, ready: sendable && rows.every((r) => r.state === 'ok'), missing };
}

/** The line under a disabled Indberet button: "Mangler 3 godkendelser, 2 prøvesvar". */
export function indberetHint(list: Checklist): string | null {
  if (list.ready) return null;
  if (list.source === 'server') return list.missing.join(' · ');
  return `Mangler ${list.missing.join(', ')}`;
}

export function rowById(list: Checklist, id: CheckId): CheckRow {
  const row = list.rows.find((r) => r.id === id);
  if (!row) throw new Error(`checklist has no row ${id}`);
  return row;
}
